import { type PostopAlert, type PostopPatient, SYMPTOMS } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Dialog, EmptyState, PageHeader, Spinner, Switch, cn } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, HeartPulse, Siren } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';

export const postopAlertsQuery = (all = false) => queryOptions({
  queryKey: ['postop', 'alerts', all], queryFn: () => api<PostopAlert[]>(`/postop/alerts${all ? '?todos=1' : ''}`), refetchInterval: 60_000,
});
const patientsQuery = queryOptions({ queryKey: ['postop', 'patients'], queryFn: () => api<PostopPatient[]>('/postop/patients') });

const SOURCE: Record<PostopAlert['source'], string> = { portal: 'Área do paciente', whatsapp: 'WhatsApp', staff: 'Equipe' };
const STATUS: Record<PostopAlert['status'], [string, 'danger' | 'warning' | 'success']> = { open: ['Aberto', 'danger'], acknowledged: ['Em atendimento', 'warning'], resolved: ['Resolvido', 'success'] };
const symptomLabel = (s: string) => (SYMPTOMS as Record<string, { label: string }>)[s]?.label ?? s;
const brDate = (iso: string) => iso.slice(0, 10).split('-').reverse().join('/');
const time = (iso: string) => new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

/** "há 12 min" que se atualiza sozinho (SLA do alerta). */
export function useElapsed(iso: string) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 30_000); return () => clearInterval(t); }, []);
  const min = Math.max(0, Math.floor((now - Date.parse(iso)) / 60_000));
  return min < 60 ? `há ${min} min` : `há ${Math.floor(min / 60)} h ${min % 60} min`;
}
function Elapsed({ iso }: { iso: string }) { return <>{useElapsed(iso)}</>; }

export function useAlertActions() {
  const qc = useQueryClient();
  const done = () => void qc.invalidateQueries({ queryKey: ['postop'] });
  const ack = useMutation({ mutationFn: (id: string) => api<void>(`/postop/alerts/${id}/acknowledge`, { method: 'POST' }), onSettled: done });
  const resolve = useMutation({ mutationFn: ({ id, resolution }: { id: string; resolution: string }) => api<void>(`/postop/alerts/${id}/resolve`, { method: 'POST', body: { resolution } }), onSuccess: done });
  return { ack, resolve };
}

function ResolveDialog({ alert, onClose }: { alert: PostopAlert | null; onClose: () => void }) {
  const { resolve } = useAlertActions();
  const [text, setText] = useState('');
  return (
    <Dialog open={!!alert} onClose={onClose} title="Resolver alerta" description={alert ? `${alert.patientName} · ${alert.symptoms.map(symptomLabel).join(', ')}` : undefined}>
      <form className="grid gap-4 p-5" onSubmit={(e) => { e.preventDefault(); if (alert) resolve.mutate({ id: alert.id, resolution: text }, { onSuccess: onClose }); }}>
        <textarea aria-label="O que foi feito" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} autoFocus
          placeholder="Ex.: Liguei para o paciente, orientei compressa e retorno amanhã às 9h."
          className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg focus:border-primary" />
        <p className="text-[12px] text-fg-muted">Fica registrado no histórico do paciente e na auditoria.</p>
        {resolve.error && <Alert tone="danger">{resolve.error.message}</Alert>}
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button type="submit" loading={resolve.isPending} disabled={text.trim().length < 3}>Resolver</Button></div>
      </form>
    </Dialog>
  );
}

function AlertRow({ a, canWrite, onResolve }: { a: PostopAlert; canWrite: boolean; onResolve: () => void }) {
  const { ack } = useAlertActions();
  return (
    <li className="grid gap-2 px-5 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start">
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-2">
          {a.severity === 'urgent' ? <Badge tone="danger">Urgente</Badge> : <Badge tone="warning">Atenção</Badge>}
          <Link to="/pacientes/$id" params={{ id: a.patientId }} className="font-medium text-fg hover:underline">{a.patientName}</Link>
          {a.day !== null && <span className="text-[12.5px] text-fg-muted">dia {a.day}</span>}
          <span className="text-[12.5px] text-fg-subtle">· {SOURCE[a.source]} · {a.status === 'resolved' ? time(a.createdAt) : <Elapsed iso={a.createdAt} />}</span>
        </p>
        <p className="mt-1 text-sm text-fg">{a.symptoms.map(symptomLabel).join(' · ')}</p>
        {a.note && <p className="mt-1 text-[13px] whitespace-pre-line text-fg-muted">“{a.note}”</p>}
        {a.acknowledgedByName && <p className="mt-1 text-[12px] text-fg-muted">Assumido por {a.acknowledgedByName}{a.acknowledgedAt ? ` · ${time(a.acknowledgedAt)}` : ''}</p>}
        {a.resolution && <p className="mt-1 text-[12.5px] text-success">Resolução: {a.resolution}</p>}
      </div>
      <div className="flex items-center gap-2">
        <Badge tone={STATUS[a.status][1]}>{STATUS[a.status][0]}</Badge>
        {canWrite && a.status === 'open' && <Button size="sm" loading={ack.isPending} onClick={() => ack.mutate(a.id)}>Assumir</Button>}
        {canWrite && a.status !== 'resolved' && <Button size="sm" variant="secondary" onClick={onResolve}>Resolver</Button>}
      </div>
      {ack.error && <div className="sm:col-span-2"><Alert tone="warning">{ack.error.message}</Alert></div>}
    </li>
  );
}

/** Pós-operatório: pacientes em acompanhamento e os alertas de possível intercorrência. */
export function PostopPage() {
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('clinical.record.write');
  const [all, setAll] = useState(false);
  const alerts = useQuery(postopAlertsQuery(all));
  const patients = useQuery(patientsQuery);
  const [resolving, setResolving] = useState<PostopAlert | null>(null);
  return (
    <div className="mx-auto grid max-w-6xl gap-7">
      <PageHeader title="Pós-operatório" description="A trilha de cada paciente (mensagens, orientações e fotos no dia certo) e os pedidos de ajuda que chegam pelo portal ou WhatsApp." />
      <Card>
        <CardHeader title="Alertas" description="Urgentes aparecem em destaque para toda a equipe clínica até alguém assumir."
          action={<Switch checked={all} onChange={setAll} label="Mostrar resolvidos" />} />
        {alerts.isLoading ? <Spinner className="m-5 size-5 text-fg-subtle" label="Carregando" /> : alerts.data?.length ? (
          <ul className="divide-y divide-border">{alerts.data.map((a) => <AlertRow key={a.id} a={a} canWrite={canWrite} onResolve={() => setResolving(a)} />)}</ul>
        ) : <p className="px-5 pb-5 text-sm text-fg-muted">Nenhum alerta {all ? '' : 'aberto '}no momento.</p>}
      </Card>
      <Card className="overflow-hidden">
        <CardHeader title="Pacientes em acompanhamento" description="O plano começa quando a ficha cirúrgica é assinada, seguindo o protocolo da clínica (Configurações › Pós-operatório)." />
        {patients.isLoading ? <Spinner className="m-5 size-5 text-fg-subtle" label="Carregando" /> : patients.data?.length ? (
          <div className="divide-y divide-border border-t border-border">
            {patients.data.map((p) => (
              <Link key={p.planId} to="/pacientes/$id" params={{ id: p.patientId }} search={{ aba: 'cirurgia' }}
                className="grid gap-2 px-5 py-3.5 transition-colors hover:bg-surface-muted sm:grid-cols-[minmax(0,1fr)_110px_minmax(0,240px)_120px] sm:items-center">
                <span className="truncate font-medium text-fg">{p.patientName}</span>
                <span className="text-[13px] text-fg-muted tabular-nums">Dia {p.day}</span>
                <span className="truncate text-[13px] text-fg-muted">{p.nextStep ? `${p.nextStep.title} · ${brDate(p.nextStep.dueOn)}` : 'Trilha concluída'}</span>
                <span className={cn('flex items-center gap-1.5 text-[13px]', p.openAlerts ? 'font-medium text-danger' : 'text-fg-subtle')}>
                  {p.openAlerts ? <><AlertTriangle className="size-3.5" />{p.openAlerts} alerta{p.openAlerts > 1 ? 's' : ''}</> : 'sem alertas'}
                </span>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState className="m-5" icon={<HeartPulse className="size-6" />} title="Ninguém em pós-operatório">
            <p>Ao assinar a ficha da cirurgia, o paciente entra aqui automaticamente.</p>
          </EmptyState>
        )}
      </Card>
      <ResolveDialog key={resolving?.id ?? 'none'} alert={resolving} onClose={() => setResolving(null)} />
    </div>
  );
}

/** Faixa fixa no topo para alertas urgentes ainda sem dono (toda a equipe clínica vê). */
export function UrgentBanner() {
  const { data: me } = useSuspenseQuery(meQuery);
  const can = me.permissions.includes('clinical.record.read');
  const canWrite = me.permissions.includes('clinical.record.write');
  const { data } = useQuery({ ...postopAlertsQuery(false), enabled: can });
  const { ack } = useAlertActions();
  const urgent = data?.filter((a) => a.severity === 'urgent' && a.status === 'open') ?? [];
  if (!urgent.length) return null;
  const a = urgent[0]!;
  return (
    <div role="alert" className="mx-4 mt-3 flex shrink-0 flex-wrap items-center gap-3 rounded-xl sm:mx-8 border border-danger/40 bg-danger-soft px-4 py-3 text-danger">
      <Siren className="size-5 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1 text-sm">
        <span className="font-semibold">Possível intercorrência: {a.patientName}</span>
        {a.day !== null && ` · dia ${a.day}`} · {a.symptoms.map(symptomLabel).join(', ')} · <Elapsed iso={a.createdAt} />
        {urgent.length > 1 && <span className="font-semibold"> · +{urgent.length - 1} urgente{urgent.length > 2 ? 's' : ''}</span>}
      </p>
      {canWrite && <Button size="sm" variant="danger" loading={ack.isPending} onClick={() => ack.mutate(a.id)}>Assumir</Button>}
      <Link to="/pos-operatorio" className="text-sm font-medium underline">Ver alertas</Link>
    </div>
  );
}
